import React, { useState, useEffect } from "react";
import { FaSearch } from "react-icons/fa";

// --- IMPORTS ---
import ClienteAutocomplete from "../../../components/autocomplete/ClienteAutocomplete";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson, appFetchBlob } from "../../../utils/http";

// --- CONSTANTES ---
const TIPO_ESTADO_TARJETA_ID = 6;

const FORMATO_OPCIONES = [
  { id: "EXCEL", label: "EXCEL" },
  { id: "PDF", label: "PDF" },
];

export default function ReporteListadoDeTarjetas() {
  const [descargando, setDescargando] = useState(false);

  // Para cargar la lista de estados manualmente
  const [listaEstados, setListaEstados] = useState([]);
  const [loadingEstados, setLoadingEstados] = useState(false);

  // --- ESTADOS DEL FORMULARIO ---
  const [cliente, setCliente] = useState(null);
  const [estadoId, setEstadoId] = useState("");
  const [fechaDesde, setFechaDesde] = useState("2010-01-01");
  const [fechaHasta, setFechaHasta] = useState("2050-12-31");
  const [formato, setFormato] = useState("EXCEL");

  // --- 1. CARGAR ESTADOS ---
  useEffect(() => {
    const cargarEstados = async () => {
      setLoadingEstados(true);
      try {
        const url = `${apiRoutes.estado.autocomplete()}?tipoId=${TIPO_ESTADO_TARJETA_ID}`;
        const data = await apiFetchJson(url);
        setListaEstados(data || []);
      } catch (error) {
        console.error("Error cargando estados:", error);
      } finally {
        setLoadingEstados(false);
      }
    };
    cargarEstados();
  }, []);

  // --- HANDLER CONSULTAR ---
  const handleConsultar = async () => {
    if (!formato) {
      alert("Seleccione un formato");
      return;
    }

    setDescargando(true);

    try {
      const params = new URLSearchParams();

      if (cliente?.id) params.append("clienteId", cliente.id);

      // Estado ID directo
      if (estadoId) params.append("estadoId", estadoId);

      if (fechaDesde) params.append("fechaDesde", fechaDesde);
      if (fechaHasta) params.append("fechaHasta", fechaHasta);

      // ✅ 2. Lógica para elegir la ruta correcta (Excel o PDF)
      const url =
        formato === "PDF"
          ? apiRoutes.tarjetas.exportarPdf(params)
          : apiRoutes.tarjetas.exportar(params);

      console.log(`Intentando descargar ${formato} desde:`, url);

      // Usamos appFetchBlob que ya maneja la autenticación y la redirección en 401
      const { blob, filename } = await appFetchBlob(
        url,
        { method: "GET" },
        { showLoading: false }, // El componente ya tiene su propio indicador de carga
      );

      // Manejo del archivo
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;

      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[-:T]/g, "");

      // ✅ 3. Extensión dinámica según el formato
      const extension = formato === "PDF" ? "pdf" : "xlsx";
      const fallbackFilename = `Reporte_Tarjetas_${timestamp}.${extension}`;

      a.download = filename || fallbackFilename;

      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      // appFetchBlob lanza un error en respuestas no-OK.
      // El caso 401 es manejado dentro de http.jsx y no debería llegar aquí.
      console.error("Error en descarga:", error);
      if (error.status !== 401) {
        alert(`No se pudo descargar: ${error.message}`);
      }
    } finally {
      setDescargando(false);
    }
  };

  return (
    <div className="w-full p-4 md:p-6">
      <div className="bg-white border rounded-md shadow-sm">
        {/* --- HEADER --- */}
        <div className="px-4 md:px-6 py-4 border-b flex flex-col md:flex-row md:items-center md:justify-between bg-gray-50 gap-2">
          <h2 className="text-lg md:text-xl font-semibold text-gray-800">
            Listado de Tarjetas
          </h2>
          
        </div>

        {/* --- BODY (FORMULARIO VERTICAL) --- */}
        <div className="p-4 md:p-6">
          <div className="bg-gray-100 p-4 md:p-8 rounded-md space-y-4 md:space-y-5 max-w-4xl mx-auto">
            {/* 1. CLIENTE */}
            <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
              <label className="md:w-52 shrink-0 font-bold text-gray-700">
                Cliente:
              </label>
              <div className="w-full">
                <ClienteAutocomplete
                  value={cliente}
                  onChange={setCliente}
                  placeholder="Buscar cliente (vacío = todos)..."
                />
              </div>
            </div>

            {/* 2. FECHA DESDE */}
            <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
              <label className="md:w-52 shrink-0 font-bold text-gray-700">
                Inicio de vigencia:
              </label>
              <div className="w-full">
                <input
                  type="date"
                  className="input w-full h-[42px] bg-white border border-gray-300 rounded px-3 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                />
              </div>
            </div>

            {/* 3. FECHA HASTA */}
            <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
              <label className="md:w-52 shrink-0 font-bold text-gray-700">
                Fin de vigencia:
              </label>
              <div className="w-full">
                <input
                  type="date"
                  className="input w-full h-[42px] bg-white border border-gray-300 rounded px-3 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                />
              </div>
            </div>

            {/* 4. ESTADO */}
            <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
              <label className="md:w-52 shrink-0 font-bold text-gray-700">
                Estado:
              </label>
              <div className="w-full">
                <select
                  className="input w-full h-[42px] bg-white border border-gray-300 rounded px-3 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-200"
                  value={estadoId}
                  onChange={(e) => setEstadoId(e.target.value)}
                  disabled={loadingEstados}
                >
                  <option value="">-- Seleccionar estado --</option>

                  {loadingEstados ? (
                    <option disabled>Cargando...</option>
                  ) : (
                    listaEstados.map((est) => (
                      <option key={est.id} value={est.id}>
                        {est.label}
                      </option>
                    ))
                  )}
                </select>
              </div>
            </div>

            {/* 5. FORMATO */}
            <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
              <label className="md:w-52 shrink-0 font-bold text-gray-700">
                Formato*:
              </label>
              <div className="w-full">
                <select
                  className="input w-full h-[42px] bg-white border border-gray-300 rounded px-3 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  value={formato}
                  onChange={(e) => setFormato(e.target.value)}
                >
                  {FORMATO_OPCIONES.map((op) => (
                    <option key={op.id} value={op.id}>
                      {op.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* --- FOOTER (BOTÓN) --- */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-start">
          <button
            onClick={handleConsultar}
            disabled={descargando || loadingEstados}
            className={`flex items-center gap-2 text-white font-semibold py-2 px-6 rounded shadow transition-colors ${
              descargando
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-green-800 hover:bg-green-900"
            }`}
          >
            {descargando ? (
              <svg
                className="animate-spin h-4 w-4 text-white"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8H4z"
                ></path>
              </svg>
            ) : (
              <FaSearch className="text-sm" />
            )}
            <span>{descargando ? "Generando Reporte..." : "Consultar"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
